import React from 'react';
import { HOME_THEME as T } from '../../lib/juniorTheme';
import { usePlaySession } from '@/play/contexts/PlaySessionContext';
import { useJuniorAliveNav } from '../../contexts/JuniorAliveNavContext';

/** Cielo despejado — fondo lúdico unificado (Eco rítmico, Atrapa el ritmo, etc.). */
export const JUNIOR_IMMERSIVE_BG =
  'linear-gradient(180deg, #E2F0FE 0%, #C4E0FB 100%)';

interface JuniorLayoutProps {
  children: React.ReactNode;
  showBar?: boolean;
  /** Pantalla completa sin scroll; app bar transparente sobre fondo unificado. */
  immersive?: boolean;
  /** Oculta la barra Junior (🌈) — modo TV/proyector. */
  presentation?: boolean;
}

export const JuniorLayout: React.FC<JuniorLayoutProps> = ({
  children,
  showBar = true,
  immersive = false,
  presentation = false,
}) => {
  const { userName, isAuthenticated, logout } = usePlaySession();
  const { navigateAlive } = useJuniorAliveNav();

  const appBar = showBar && !presentation && (
    <header
      className={`junior-app-bar relative z-20 flex items-center justify-between px-4 sm:px-6 ${
        immersive ? 'shrink-0 py-2 sm:py-3' : 'py-4'
      }`}
    >
      <button
        type="button"
        onClick={() => navigateAlive('/home')}
        className="junior-display text-2xl sm:text-3xl tracking-tight hover:opacity-80 transition-opacity inline-flex items-center gap-2"
        style={{ color: immersive ? '#1E4976' : T.accent }}
      >
        <span aria-hidden>🌈</span> Junior
      </button>
      <div className="flex items-center gap-3">
        {userName && (
          <span
            className="text-sm hidden sm:inline font-bold"
            style={{ color: immersive ? '#1E4976' : T.muted }}
          >
            {userName}
          </span>
        )}
        {isAuthenticated && (
          <button
            type="button"
            onClick={() => logout().then(() => navigateAlive('/'))}
            className="text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white/50 transition-colors"
            style={{ color: immersive ? '#2563EB' : T.muted }}
          >
            Salir
          </button>
        )}
      </div>
    </header>
  );

  if (immersive) {
    return (
      <div
        className={`junior-layout-immersive layout-principal flex flex-col h-[100dvh] max-h-[100dvh] w-full overflow-hidden${
          presentation ? ' junior-layout--presentation' : ''
        }`}
        style={{ background: JUNIOR_IMMERSIVE_BG, color: T.text }}
      >
        {appBar}
        <main className="relative z-10 flex flex-col flex-1 min-h-0 overflow-hidden">{children}</main>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ background: T.bgGradient, color: T.text }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-45"
        aria-hidden
        style={{
          background:
            'radial-gradient(circle at 15% 85%, rgba(244,114,182,0.3), transparent 45%), radial-gradient(circle at 85% 15%, rgba(14,165,233,0.28), transparent 40%), radial-gradient(circle at 50% 50%, rgba(132,204,22,0.12), transparent 50%)',
        }}
      />
      {appBar}
      <main className="relative z-10">{children}</main>
    </div>
  );
};

export default JuniorLayout;
